import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';


export default function SharePlanInviteModal({ onResolved }) {
  const { t } = useTranslation();
  const [invite, setInvite] = useState(null);
  const [responding, setResponding] = useState(false);

  const checkInvites = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/share-plan/pending-invites`, { withCredentials: true });
      const invites = res.data.invites || [];
      if (invites.length > 0) {
        setInvite(invites[0]);
      }
    } catch {
      // Not logged in or no invites
    }
  }, []);

  useEffect(() => {
    checkInvites();
  }, [checkInvites]);

  const handleRespond = async (accept) => {
    if (!invite) return;
    setResponding(true);
    try {
      await axios.post(`${API}/share-plan/respond`, {
        owner_id: invite.owner_id,
        accept,
      }, { withCredentials: true });
      toast.success(accept ? t('invite.accepted') : t('invite.declined'));
      setInvite(null);
      if (accept) {
        window.dispatchEvent(new Event('subscription-changed'));
      }
      if (onResolved) onResolved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('invite.failed'));
    } finally {
      setResponding(false);
    }
  };

  if (!invite) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="share-plan-invite-modal"
    >
      <div
        className="w-full max-w-sm rounded-2xl p-6 text-center space-y-5"
        style={{ background: 'var(--theme-surface)', border: '1px solid var(--glass-border)' }}
      >
        <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center" style={{ background: '#10b98122' }}>
          <Users className="h-7 w-7" style={{ color: '#10b981' }} />
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-1" style={{ color: 'var(--theme-text)' }}>
            {t('invite.title')}
          </h3>
          <p className="text-sm" style={{ color: 'var(--theme-textSecondary)' }}>
            <strong style={{ color: 'var(--theme-text)' }}>{invite.owner_name}</strong> {t('invite.description', { name: '' }).replace('{{name}} ', '')}
          </p>
          <p className="text-xs mt-2" style={{ color: 'var(--theme-textSecondary)' }}>
            {t('invite.access_info')}
          </p>
        </div>

        <div className="flex gap-3">
          <Button
            onClick={() => handleRespond(false)}
            disabled={responding}
            variant="outline"
            className="flex-1 rounded-xl py-5 gap-2"
            style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
            data-testid="invite-deny-btn"
          >
            <X className="h-4 w-4" /> {t('invite.deny')}
          </Button>
          <Button
            onClick={() => handleRespond(true)}
            disabled={responding}
            className="flex-1 rounded-xl py-5 gap-2"
            style={{ background: '#10b981', color: '#fff' }}
            data-testid="invite-accept-btn"
          >
            <Check className="h-4 w-4" /> {t('invite.accept')}
          </Button>
        </div>
      </div>
    </div>
  );
}
